import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { fetcher } from "@/lib/api";
import GeoMap from "@/components/geoportal/GeoMap";
import GeoFilters from "@/components/geoportal/GeoFilters";
import GeoDetailDrawer from "@/components/geoportal/GeoDetailDrawer";
import { useAuth } from "@/contexts/AuthContext";
import GeoKpiStrip from "@/components/geoportal/GeoKpiStrip";
import ScopeBanner from "@/components/salnes/ScopeBanner";
import { Skeleton } from "@/components/ui/skeleton";
import "@/i18n/fase2";

export default function Geoportal() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selection, setSelection] = useState(null);
  const [filters, setFilters] = useState({
    concello: "all",
    range: "30d",
    from: "",
    to: "",
    date: "",
    show: "all",
    include_inactive: false, // Fase 2: markers sin actividad ocultos por defecto
    weighins_mode_events: false, // iter48: aggregated por defecto
  });
  const [layers, setLayers] = useState(() => {
    // Persist layer state so the user doesn't need to re-toggle on every visit.
    try {
      const saved = JSON.parse(localStorage.getItem("geoportal_layers") || "null");
      if (saved && typeof saved === "object") {
        return {
          detections: true, overflow: true, observations: true,
          vehicles: true, concellos: true, sigeus_points: false,
          weighins: false,
          ...saved,
        };
      }
    } catch {
      /* ignore */
    }
    return {
      detections: true,
      overflow: true,
      observations: true,
      vehicles: true,
      concellos: true,
      sigeus_points: false,
      weighins: false,
    };
  });
  const [basemap, setBasemap] = useState("carto");

  // iter45/48 · construir query string temporal reutilizable en SWR keys.
  const rangeQs = useMemo(() => {
    if (filters.range === "day") {
      if (!filters.date) return null;
      return `date=${filters.date}`;
    }
    if (filters.range === "custom") {
      if (filters.from && filters.to && filters.from > filters.to) return null;
      const parts = ["range=custom"];
      if (filters.from) parts.push(`from=${filters.from}`);
      if (filters.to) parts.push(`to=${filters.to}`);
      return parts.join("&");
    }
    return `range=${filters.range}`;
  }, [filters.range, filters.from, filters.to, filters.date]);

  const { data: kpis } = useSWR("/salnes/kpis", fetcher, { refreshInterval: 60000 });
  const { data: mapData, isLoading: loadingPoints } = useSWR(
    rangeQs ? `/salnes/map-points?limit=2000&${rangeQs}` : null,
    fetcher,
    { refreshInterval: 60000, revalidateOnFocus: false }
  );
  const { data: municipalities } = useSWR("/salnes/municipalities", fetcher);
  const { data: vehicles } = useSWR("/salnes/vehicles", fetcher, { refreshInterval: 90000 });
  const { data: detectionsList } = useSWR(
    "/salnes/detections?limit=200",
    fetcher,
    { refreshInterval: 90000, revalidateOnFocus: false }
  );
  // siGEUS collection points — only fetch when layer is enabled (1065 points)
  const { data: sigeusData } = useSWR(
    layers.sigeus_points ? "/sigeus/col-points?limit=2000" : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  // iter45/48 · pesajes. Default aggregated (1 marker por contenedor).
  // Con toggle `weighins_mode_events` cambia a mode=events (histórico).
  const weighinsMode = filters.weighins_mode_events ? "events" : "aggregated";
  const { data: weighinsData } = useSWR(
    layers.weighins && rangeQs
      ? `/salnes/weighins-map?limit=3000&${rangeQs}&mode=${weighinsMode}`
      : null,
    fetcher,
    { refreshInterval: 120000, revalidateOnFocus: false }
  );

  // Persist layer selection
  useEffect(() => {
    try {
      localStorage.setItem("geoportal_layers", JSON.stringify(layers));
    } catch {
      /* ignore quota errors */
    }
  }, [layers]);

  const concellos = municipalities?.concellos || [];
  const points = useMemo(() => {
    const raw = mapData?.points || [];
    // Filtro "sin actividad reciente": si include_inactive == false y el marker
    // tiene bandera `no_recent_activity` desde upstream, se oculta. Los markers
    // sin la bandera (los que sí tienen datos frescos) siempre se ven.
    let filtered = raw;
    if (!filters.include_inactive) {
      filtered = filtered.filter((p) => !p.no_recent_activity);
    }
    if (filters.show === "overflow") return filtered.filter((p) => p.overflow);
    if (filters.show === "observations") return filtered.filter((p) => !p.overflow);
    return filtered;
  }, [mapData, filters.show, filters.include_inactive]);

  const now = new Date();
  const updatedAt = `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  const openDetail = (p) => {
    const full = (detectionsList?.detections || []).find((d) => d.object_id === p.id);
    setSelection(full ? { ...full, ...p } : p);
  };

  const noFleet = !vehicles?.vehicles?.length;

  return (
    <div className="fixed inset-0 lg:left-[248px] top-16 flex flex-col bg-salnes-ivory" data-testid="geoportal-root">
      <div className="px-6 lg:px-10 pt-4 pb-2 bg-white border-b hairline flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-salnes-emerald">
            {t("fase2.nav_geoportal")}
          </div>
          <h1 className="font-serif text-2xl text-salnes-ink leading-tight">
            {t("fase2.geoportal_title")}
          </h1>
        </div>
        {noFleet && (
          <div className="hidden md:block max-w-md">
            <ScopeBanner variant="warn">{t("fase2.fleet_empty_notice")}</ScopeBanner>
          </div>
        )}
      </div>

      <GeoKpiStrip
        kpis={kpis}
        updatedAt={updatedAt}
        layers={layers}
        onToggleLayer={(k) => setLayers((prev) => ({ ...prev, [k]: !prev[k] }))}
      />

      <div className="flex-1 flex overflow-hidden">
        <GeoFilters
          concellos={concellos}
          filters={filters}
          onFiltersChange={setFilters}
          layers={layers}
          onLayersChange={setLayers}
          basemap={basemap}
          onBasemapChange={setBasemap}
        />
        <div className="flex-1 relative">
          {loadingPoints && !mapData ? (
            <div className="p-6 grid gap-3">
              <Skeleton className="h-96 w-full" />
            </div>
          ) : (
            <GeoMap
              points={points}
              concellos={concellos}
              vehicles={vehicles?.vehicles || []}
              sigeusPoints={sigeusData?.col_points || []}
              weighins={weighinsData?.points || []}
              weighinsMode={weighinsData?.mode || weighinsMode}
              layers={layers}
              basemap={basemap}
              onSelect={openDetail}
              viewDetailLabel={t("fase2.view_detail")}
              sigeusDetailLabel={t("fase2.sigeus_popup_view_detail")}
            />
          )}
          <div className="absolute bottom-4 left-4 z-[400] max-w-[380px]">
            <div className="glass rounded-xl p-3 text-[11px] text-salnes-ink/80 border hairline">
              {t("fase2.no_polygons_notice")}
            </div>
          </div>
        </div>
      </div>

      <GeoDetailDrawer
        detail={selection}
        open={!!selection}
        onClose={() => setSelection(null)}
        role={user?.role}
      />
    </div>
  );
}
